import React, { useState } from 'react';
import {
  Modal,
  Button,
  List,
  Card,
  Space,
  Tag,
  Typography,
  message,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  FileTextOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { ApplicationCargo, Document } from '../../types';
import {
  useGetDocumentsByCargoQuery,
  useDeleteDocumentMutation
} from '../../store/api/documentsApi';
import UploadDocumentModal from '../documents/UploadDocumentModal';
import DocumentAuthImage from '../documents/DocumentAuthImage';
import {
  downloadDocument,
  fetchDocumentObjectUrl,
  resolveDocumentFilename,
  documentPreviewEndpoint,
} from '../../utils/documentDownload';

const { Text, Title } = Typography;

interface CargoDocumentsModalProps {
  visible: boolean;
  cargo: ApplicationCargo | null;
  onClose: () => void;
}

const CargoDocumentsModal: React.FC<CargoDocumentsModalProps> = ({
  visible,
  cargo,
  onClose
}) => {
  const [uploadModalVisible, setUploadModalVisible] = useState(false);

  const { data: documents = [], refetch: refetchDocuments } = useGetDocumentsByCargoQuery(
    cargo?.id || 0,
    { skip: !cargo?.id }
  );
  const [deleteDocument] = useDeleteDocumentMutation();

  const handleUploadSuccess = () => {
    refetchDocuments();
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await deleteDocument(documentId).unwrap();
      message.success('Фотография успешно удалена');
      refetchDocuments();
    } catch (error: any) {
      console.error('Delete error:', error);
      const errorMessage = error?.data?.error || error?.message || 'Ошибка при удалении фотографии';
      message.error(errorMessage);
    }
  };

  const files = documents.filter((doc: Document) => doc.fileType === 'file');
  const photos = documents.filter((doc: Document) => doc.fileType === 'photo');

  const handleViewDocument = async (document: Document) => {
    try {
      const displayName = resolveDocumentFilename(document);
      const endpoint = documentPreviewEndpoint(document.fileType);
      const { url, revoke } = await fetchDocumentObjectUrl(document.id, endpoint);

      if (document.fileType === 'photo') {
        Modal.info({
          title: `Просмотр: ${displayName}`,
          content: (
            <div style={{ textAlign: 'center' }}>
              <img
                src={url}
                alt={displayName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
              />
            </div>
          ),
          width: 800,
          okText: 'Закрыть',
          afterClose: revoke,
        });
      } else {
        Modal.info({
          title: `Просмотр: ${displayName}`,
          content: (
            <div style={{ width: '100%', height: '600px' }}>
              <iframe
                src={url}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="File preview"
              />
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <Button
                  type="primary"
                  onClick={() => {
                    const opened = window.open(url, '_blank', 'noopener,noreferrer');
                    if (!opened) {
                      message.warning('Браузер заблокировал открытие вкладки. Разрешите pop-up для сайта.');
                    }
                  }}
                >
                  Открыть в новой вкладке
                </Button>
              </div>
            </div>
          ),
          width: 1000,
          okText: 'Закрыть',
          afterClose: revoke,
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      message.error('Ошибка при загрузке файла');
    }
  };

  const handleDownloadDocument = async (document: Document) => {
    try {
      await downloadDocument(document.id, document);
    } catch (error) {
      console.error('Ошибка скачивания файла:', error);
      message.error('Не удалось скачать файл');
    }
  };

  return (
    <Modal
      title={`Фотографии груза: ${cargo?.name || `ID: ${cargo?.id}`}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnHidden
    >
      <div style={{ marginBottom: '24px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setUploadModalVisible(true)}
          disabled={!cargo}
        >
          Загрузить документ
        </Button>
      </div>

      <UploadDocumentModal
        visible={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        onSuccess={handleUploadSuccess}
        cargo={cargo || undefined}
      />

      {files.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <Title level={4}>Документы ({files.length})</Title>
          <List
            grid={{ gutter: 16, column: 2 }}
            dataSource={files}
            renderItem={(document: Document) => (
              <List.Item>
                <Card
                  size="small"
                  actions={[
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => void handleViewDocument(document)}
                    >
                      Просмотр
                    </Button>,
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => void handleDownloadDocument(document)}
                    >
                      Скачать
                    </Button>,
                    <Popconfirm
                      title="Удалить документ?"
                      onConfirm={() => handleDeleteDocument(document.id)}
                      okText="Да"
                      cancelText="Нет"
                    >
                      <Button type="link" danger icon={<DeleteOutlined />}>
                        Удалить
                      </Button>
                    </Popconfirm>
                  ]}
                >
                  <Card.Meta
                    avatar={<FileTextOutlined style={{ fontSize: '24px' }} />}
                    title={resolveDocumentFilename(document)}
                    description={
                      <Space direction="vertical" size="small">
                        <div>
                          <Tag color="blue">{document.stage}</Tag>
                          {document.comment && <Text type="secondary">{document.comment}</Text>}
                        </div>
                        <Text type="secondary">
                          {new Date(document.createdAt).toLocaleDateString('ru-RU')}
                        </Text>
                      </Space>
                    }
                  />
                </Card>
              </List.Item>
            )}
          />
        </div>
      )}

      <div>
        <Title level={4}>Фотографии ({photos.length})</Title>
        <List
          grid={{ gutter: 16, column: 3 }}
          dataSource={photos}
          renderItem={(document: Document) => (
            <List.Item>
              <Card
                size="small"
                cover={
                  <DocumentAuthImage
                    documentId={document.id}
                    fileType={document.fileType}
                    alt={resolveDocumentFilename(document)}
                  />
                }
                actions={[
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => void handleViewDocument(document)}
                  >
                    Просмотр
                  </Button>,
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => void handleDownloadDocument(document)}
                  >
                    Скачать
                  </Button>,
                  <Popconfirm
                    title="Удалить фотографию?"
                    onConfirm={() => handleDeleteDocument(document.id)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <Button type="link" danger icon={<DeleteOutlined />}>
                      Удалить
                    </Button>
                  </Popconfirm>
                ]}
              >
                <Card.Meta
                  title={resolveDocumentFilename(document)}
                  description={
                    <Space direction="vertical" size="small">
                      <div>
                        <Tag color="green">{document.stage}</Tag>
                        {document.comment && <Text type="secondary">{document.comment}</Text>}
                      </div>
                      <Text type="secondary">
                        {new Date(document.createdAt).toLocaleDateString('ru-RU')}
                      </Text>
                    </Space>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};

export default CargoDocumentsModal;
